/**
 * PRD-15: Active Account Store
 *
 * Manages the active broker account state for the current user.
 * Syncs with server via /accounts/context/ endpoints.
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { AdaptedAccount } from "../types/account.adapted.types";
interface ActiveAccountState {
  // Server-synced state
  activeAccountId: number | null;
  primaryAccountId: number | null;

  // Derived state (populated from accounts list)
  activeAccount: AdaptedAccount | null;
  primaryAccount: AdaptedAccount | null;

  // Loading states
  isLoading: boolean;
  isSyncing: boolean;

  // Actions
  setActiveAccountId: (id: number | null) => void;
  setPrimaryAccountId: (id: number | null) => void;
  setContext: (context: {
    activeAccountId?: number | null;
    primaryAccountId?: number | null;
  }) => void;
  setAccounts: (active: AdaptedAccount | null, primary: AdaptedAccount | null) => void;
  setIsLoading: (loading: boolean) => void;
  setIsSyncing: (syncing: boolean) => void;
  reset: () => void;
}
const initialState = {
  activeAccountId: null,
  primaryAccountId: null,
  activeAccount: null,
  primaryAccount: null,
  isLoading: false,
  isSyncing: false
};
export const useActiveAccountStore = create<ActiveAccountState>()(devtools(persist(set => ({
  ...initialState,
  setActiveAccountId: id => set({
    activeAccountId: id
  }, false, "setActiveAccountId"),
  setPrimaryAccountId: id => set({
    primaryAccountId: id
  }, false, "setPrimaryAccountId"),
  setContext: context => set(state => ({
    activeAccountId: "activeAccountId" in context ? context.activeAccountId ?? null : state.activeAccountId,
    primaryAccountId: "primaryAccountId" in context ? context.primaryAccountId ?? null : state.primaryAccountId
  }), false, "setContext"),
  setAccounts: (active, primary) => set({
    activeAccount: active,
    primaryAccount: primary
  }, false, "setAccounts"),
  setIsLoading: loading => set({
    isLoading: loading
  }, false, "setIsLoading"),
  setIsSyncing: syncing => set({
    isSyncing: syncing
  }, false, "setIsSyncing"),
  reset: () => set(initialState, false, "reset")
}), {
  name: "sigmavue-active-account",
  // Only persist the IDs, not the full account objects
  partialize: state => ({
    activeAccountId: state.activeAccountId,
    primaryAccountId: state.primaryAccountId
  })
}), {
  name: "ActiveAccountStore"
}));

// Selector hooks for convenience
export const useActiveAccount = () => {
  return useActiveAccountStore(_temp);
};
export const usePrimaryAccount = () => {
  return useActiveAccountStore(_temp2);
};
export const useActiveAccountId = () => {
  return useActiveAccountStore(_temp3);
};
export const usePrimaryAccountId = () => {
  return useActiveAccountStore(_temp4);
};
export const useEffectiveAccount = () => {
  return useActiveAccountStore(_temp5);
};
export const useEffectiveAccountId = () => {
  return useActiveAccountStore(_temp6);
};
export const useIsAccountSyncing = () => {
  return useActiveAccountStore(_temp7);
};
export const useIsAccountLoading = () => {
  return useActiveAccountStore(_temp8);
};

// Action Hooks (for granular action subscriptions)
export const useSetActiveAccountId = () => {
  return useActiveAccountStore(_temp9);
};
export const useSetPrimaryAccountId = () => {
  return useActiveAccountStore(_temp10);
};

// Note: useSetAccountContext is exported from use-account-context.ts as a mutation hook
// This selector provides direct store access for internal use only
export const useSetAccountContextStore = () => {
  return useActiveAccountStore(_temp11);
};
export const useResetActiveAccount = () => {
  return useActiveAccountStore(_temp12);
};
function _temp(state) {
  return state.activeAccount;
}
function _temp2(state) {
  return state.primaryAccount;
}
function _temp3(state) {
  return state.activeAccountId;
}
function _temp4(state) {
  return state.primaryAccountId;
}
function _temp5(state) {
  return state.activeAccount ?? state.primaryAccount;
}
function _temp6(state) {
  return state.activeAccountId ?? state.primaryAccountId;
}
function _temp7(state) {
  return state.isSyncing;
}
function _temp8(state) {
  return state.isLoading;
}
function _temp9(state) {
  return state.setActiveAccountId;
}
function _temp10(state) {
  return state.setPrimaryAccountId;
}
function _temp11(state) {
  return state.setContext;
}
function _temp12(state) {
  return state.reset;
}